/**
 * Assets — company asset management + maintenance.
 * Tabs: Dashboard · Registry · Maintenance
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useWorkspacePerms } from "@/lib/useWorkspacePerms";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Package, Plus, Search, Edit3, Trash2, UserPlus, ArrowLeftRight, Wrench, AlertTriangle, Layers, TrendingDown, CheckCircle2, Clock, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import AssetInspectionsTab from "@/components/assets/AssetInspectionsTab";
import AssetAttachments from "@/components/assets/AssetAttachments";
import { apiErrorMessage } from "@/lib/apiError";

const STATUS_STYLE = {
  in_stock: "border-emerald-500/40 text-emerald-700",
  assigned: "border-blue-500/40 text-blue-700",
  in_repair: "border-amber-500/40 text-amber-700",
  retired: "border-foreground/25 text-foreground/60",
  lost: "border-red-500/40 text-red-700",
  disposed: "border-foreground/25 text-foreground/50",
};

export default function Assets() {
  const { activeWorkspaceId } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [tab, setTab] = useState(sp.get("tab") || "dashboard");
  const [catalog, setCatalog] = useState(null);
  useEffect(() => { setSp((prev) => { const n = new URLSearchParams(prev); n.set("tab", tab); return n; }, { replace: true }); }, [tab, setSp]);
  useEffect(() => { api.get(`/asset-catalog`).then((r) => setCatalog(r.data)); }, []);

  return (
    <div className="space-y-6" data-testid="assets-page">
      <PageHeader title="Assets" subtitle="Company property, IT devices, licenses, vehicles — full lifecycle + maintenance." icon={Package} />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-none flex-wrap h-auto bg-transparent p-0 border-b border-border w-full justify-start gap-1">
          <TabsTrigger value="dashboard" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="assets-tab-dashboard"><Layers className="w-4 h-4 mr-2" />Dashboard</TabsTrigger>
          <TabsTrigger value="registry" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="assets-tab-registry"><Package className="w-4 h-4 mr-2" />Registry</TabsTrigger>
          <TabsTrigger value="maintenance" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="assets-tab-maintenance"><Wrench className="w-4 h-4 mr-2" />Maintenance</TabsTrigger>
          <TabsTrigger value="inspections" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="assets-tab-inspections"><ClipboardCheck className="w-4 h-4 mr-2" />Inspections</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard" className="mt-6"><Dashboard wsId={activeWorkspaceId} /></TabsContent>
        <TabsContent value="registry" className="mt-6"><Registry wsId={activeWorkspaceId} catalog={catalog} /></TabsContent>
        <TabsContent value="maintenance" className="mt-6"><MaintenanceQueue wsId={activeWorkspaceId} catalog={catalog} /></TabsContent>
        <TabsContent value="inspections" className="mt-6"><AssetInspectionsTab wsId={activeWorkspaceId} /></TabsContent>
      </Tabs>
    </div>
  );
}

// ───────────────────── Dashboard ─────────────────────

function Dashboard({ wsId }) {
  const [d, setD] = useState(null);
  useEffect(() => { if (wsId) api.get(`/workspaces/${wsId}/assets/analytics`).then((r) => setD(r.data)); }, [wsId]);
  if (!d) return <Card className="rounded-none"><CardContent className="p-6 text-sm text-foreground/60">Loading…</CardContent></Card>;
  const s = d.summary;
  return (
    <div className="space-y-5" data-testid="assets-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Package} label="Total assets" value={s.total_assets} testid="ast-stat-total" />
        <Stat icon={CheckCircle2} label="Assigned" value={s.assigned} testid="ast-stat-assigned" />
        <Stat icon={Package} label="In stock" value={s.in_stock} testid="ast-stat-stock" />
        <Stat icon={Wrench} label="In repair" value={s.in_repair} testid="ast-stat-repair" />
        <Stat icon={TrendingDown} label="Purchase value" value={fmtMoney(s.total_value)} testid="ast-stat-value" />
        <Stat icon={TrendingDown} label="Book value (depr.)" value={fmtMoney(s.current_book_value)} testid="ast-stat-book" tone="warn" />
        <Stat icon={AlertTriangle} label="Overdue maintenance" value={s.overdue_maintenance} tone={s.overdue_maintenance ? "danger" : "ok"} testid="ast-stat-overdue" />
        <Stat icon={Clock} label="Warranty expiring 60d" value={s.warranty_expiring_soon} tone={s.warranty_expiring_soon ? "warn" : "ok"} testid="ast-stat-warranty" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <BarCard title="By status" items={d.by_status} labelKey="status" testid="ast-by-status" />
        <BarCard title="By category" items={d.by_category} labelKey="category" testid="ast-by-category" />
        <BarCard title="By condition" items={d.by_condition} labelKey="condition" testid="ast-by-condition" />
      </div>
      {d.warranty_soon.length > 0 && (
        <Card className="rounded-none">
          <CardContent className="p-5">
            <h4 className="font-bold text-sm mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-amber-600" /> Warranty expiring in the next 60 days</h4>
            <div className="border border-border divide-y divide-border text-sm" data-testid="ast-warranty-list">
              {d.warranty_soon.map((a) => (
                <div key={a.id} className="px-3 py-2 flex items-center justify-between" data-testid={`ast-warranty-${a.id}`}>
                  <div><strong>{a.name}</strong> <span className="text-foreground/55">· {a.asset_tag}</span></div>
                  <span className="text-xs font-mono text-amber-700">{a.warranty_end}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, testid, tone }) {
  const t = { ok: "border-emerald-500/30", warn: "border-amber-500/30", danger: "border-red-500/40" }[tone] || "";
  return (
    <Card className={`rounded-none ${t}`} data-testid={testid}><CardContent className="p-4 flex items-center gap-3">
      <div className="w-9 h-9 border border-border flex items-center justify-center text-foreground/60"><Icon className="w-4 h-4" /></div>
      <div><div className="text-[10px] uppercase tracking-widest text-foreground/55">{label}</div><div className="text-2xl font-bold leading-none mt-1">{value}</div></div>
    </CardContent></Card>
  );
}

function BarCard({ title, items, labelKey, testid }) {
  if (!items?.length) return <Card className="rounded-none"><CardContent className="p-5"><h4 className="font-bold text-sm mb-3">{title}</h4><p className="text-xs text-foreground/55" data-testid={`${testid}-empty`}>No data.</p></CardContent></Card>;
  const max = Math.max(...items.map((i) => i.count), 1);
  return (
    <Card className="rounded-none"><CardContent className="p-5">
      <h4 className="font-bold text-sm mb-3">{title}</h4>
      <ul className="space-y-2" data-testid={testid}>{items.map((it) => (
        <li key={it[labelKey]} className="text-sm">
          <div className="flex items-center justify-between text-xs mb-1"><span className="truncate font-medium">{String(it[labelKey]).replace(/_/g, " ")}</span><span className="text-foreground/60 font-mono">{it.count}</span></div>
          <div className="h-2 bg-foreground/10"><div className="h-full bg-primary" style={{ width: `${(it.count / max) * 100}%` }} /></div>
        </li>
      ))}</ul>
    </CardContent></Card>
  );
}

function fmtMoney(v) { return v?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || "0"; }

// ───────────────────── Registry ─────────────────────

function Registry({ wsId, catalog }) {
  const { can } = useWorkspacePerms();
  const canManage = can("asset.manage");
  const canAssign = can("asset.assign") || canManage;
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [editing, setEditing] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = () => {
    if (!wsId) return;
    const p = new URLSearchParams();
    if (status) p.set("status", status);
    if (category) p.set("category", category);
    if (q.trim()) p.set("q", q.trim());
    api.get(`/workspaces/${wsId}/assets?${p.toString()}`).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId, status, category]);

  return (
    <div className="space-y-4" data-testid="assets-registry">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/50" />
          <Input className="pl-9 h-9 rounded-none" placeholder="Search name, tag, serial, brand…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") load(); }} data-testid="assets-search" />
        </div>
        <Select value={status || "_all"} onValueChange={(v) => setStatus(v === "_all" ? "" : v)}>
          <SelectTrigger className="h-9 w-40 rounded-none" data-testid="assets-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent><SelectItem value="_all">All statuses</SelectItem>{(catalog?.statuses || []).map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={category || "_all"} onValueChange={(v) => setCategory(v === "_all" ? "" : v)}>
          <SelectTrigger className="h-9 w-52 rounded-none" data-testid="assets-category-filter"><SelectValue placeholder="All categories" /></SelectTrigger>
          <SelectContent><SelectItem value="_all">All categories</SelectItem>{(catalog?.categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        {canManage && <Button className="rounded-none" onClick={() => setEditing({})} data-testid="assets-new-btn"><Plus className="w-4 h-4 mr-2" />New asset</Button>}
      </div>

      {!rows ? <Card className="rounded-none"><CardContent className="p-6 text-sm text-foreground/60">Loading…</CardContent></Card>
        : rows.length === 0 ? <Card className="rounded-none border-dashed"><CardContent className="p-10 text-center text-sm text-foreground/60" data-testid="assets-empty">No assets match. {canManage && "Add your first one with the button above."}</CardContent></Card>
        : (
          <div className="border border-border" data-testid="assets-table">
            <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-foreground/5 text-[10px] uppercase tracking-widest text-foreground/55 font-semibold">
              <div className="col-span-1">Tag</div>
              <div className="col-span-3">Name</div>
              <div className="col-span-2">Category</div>
              <div className="col-span-2">Assigned to</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-1">Condition</div>
              <div className="col-span-1">Value</div>
              <div className="col-span-1 text-right">Action</div>
            </div>
            {rows.map((a) => (
              <div key={a.id} className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-border items-center text-sm hover:bg-foreground/5 cursor-pointer" data-testid={`asset-row-${a.id}`} onClick={(e) => { if (!e.target.closest("button")) setDetail(a); }}>
                <div className="col-span-1 font-mono text-xs">{a.asset_tag}</div>
                <div className="col-span-3 min-w-0">
                  <div className="font-medium truncate">{a.name}</div>
                  <div className="text-[10px] text-foreground/55 truncate">{[a.brand, a.model, a.serial_number].filter(Boolean).join(" · ")}</div>
                </div>
                <div className="col-span-2 text-xs truncate">{a.category}</div>
                <div className="col-span-2 text-xs truncate">{a.assigned_to?.name || (a.status === "assigned" ? "—" : "")}</div>
                <div className="col-span-1"><Badge variant="outline" className={`rounded-none text-[10px] ${STATUS_STYLE[a.status] || ""}`}>{(a.status || "").replace(/_/g, " ")}</Badge></div>
                <div className="col-span-1 text-xs">{a.condition || "—"}</div>
                <div className="col-span-1 text-xs font-mono">{a.purchase_cost ? fmtMoney(a.purchase_cost) : "—"}</div>
                <div className="col-span-1 text-right flex gap-1 justify-end">
                  {canAssign && a.status !== "assigned" && a.status !== "retired" && a.status !== "disposed" && (
                    <Button size="sm" variant="ghost" className="rounded-none h-7 px-2" title="Assign" onClick={() => setAssigning({ asset: a, mode: "assign" })} data-testid={`asset-assign-${a.id}`}><UserPlus className="w-3 h-3" /></Button>
                  )}
                  {canAssign && a.status === "assigned" && (
                    <Button size="sm" variant="ghost" className="rounded-none h-7 px-2" title="Return" onClick={() => setAssigning({ asset: a, mode: "return" })} data-testid={`asset-return-${a.id}`}><ArrowLeftRight className="w-3 h-3" /></Button>
                  )}
                  {canManage && <Button size="sm" variant="ghost" className="rounded-none h-7 px-2" onClick={() => setEditing(a)} data-testid={`asset-edit-${a.id}`}><Edit3 className="w-3 h-3" /></Button>}
                  {canManage && <Button size="sm" variant="ghost" className="rounded-none h-7 px-2 text-destructive hover:text-destructive" onClick={async (e) => { e.stopPropagation(); if (window.confirm(`Delete ${a.name}?`)) { await api.delete(`/assets/${a.id}`); toast.success("Removed"); load(); } }} data-testid={`asset-delete-${a.id}`}><Trash2 className="w-3 h-3" /></Button>}
                </div>
              </div>
            ))}
          </div>
        )}

      {editing && <AssetEditor wsId={wsId} asset={editing} catalog={catalog} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {assigning && <AssignDialog wsId={wsId} asset={assigning.asset} mode={assigning.mode} catalog={catalog} onClose={() => setAssigning(null)} onDone={() => { setAssigning(null); load(); }} />}
      {detail && <AssetDetailDialog assetId={detail.id} catalog={catalog} canManage={canManage} onClose={() => setDetail(null)} onChange={load} />}
    </div>
  );
}

function AssetEditor({ wsId, asset, catalog, onClose, onSaved }) {
  const isNew = !asset?.id;
  const [f, setF] = useState(() => ({
    asset_tag: asset?.asset_tag || "",
    name: asset?.name || "",
    category: asset?.category || (catalog?.categories?.[0] || "Other"),
    subcategory: asset?.subcategory || "",
    status: asset?.status || "in_stock",
    condition: asset?.condition || "good",
    serial_number: asset?.serial_number || "",
    brand: asset?.brand || "",
    model: asset?.model || "",
    location: asset?.location || "",
    purchase_date: asset?.purchase_date || "",
    purchase_cost: asset?.purchase_cost ?? "",
    currency: asset?.currency || "INR",
    vendor_name: asset?.vendor_name || "",
    vendor_contact: asset?.vendor_contact || "",
    invoice_no: asset?.invoice_no || "",
    warranty_end: asset?.warranty_end || "",
    depreciation_years: asset?.depreciation_years ?? "",
    notes: asset?.notes || "",
  }));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const payload = { ...f };
      ["purchase_cost", "depreciation_years"].forEach((k) => { if (payload[k] === "" || payload[k] === null) payload[k] = null; else payload[k] = Number(payload[k]); });
      if (isNew) await api.post(`/workspaces/${wsId}/assets`, payload);
      else await api.patch(`/assets/${asset.id}`, payload);
      toast.success(isNew ? "Asset added" : "Asset updated");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="asset-editor-dialog">
        <DialogHeader><DialogTitle>{isNew ? "New asset" : `Edit ${asset.name}`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <F label="Asset tag" value={f.asset_tag} onChange={(v) => set("asset_tag", v)} placeholder="AST-00001 (auto)" testid="ast-tag" />
            <F label="Name *" value={f.name} onChange={(v) => set("name", v)} testid="ast-name" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <SelF label="Category" value={f.category} onChange={(v) => set("category", v)} options={(catalog?.categories || []).map((c) => ({ v: c, label: c }))} testid="ast-cat" />
            <F label="Subcategory" value={f.subcategory} onChange={(v) => set("subcategory", v)} testid="ast-subcat" />
            <SelF label="Status" value={f.status} onChange={(v) => set("status", v)} options={(catalog?.statuses || []).map((s) => ({ v: s, label: s.replace(/_/g, " ") }))} testid="ast-status" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <F label="Brand" value={f.brand} onChange={(v) => set("brand", v)} testid="ast-brand" />
            <F label="Model" value={f.model} onChange={(v) => set("model", v)} testid="ast-model" />
            <F label="Serial number" value={f.serial_number} onChange={(v) => set("serial_number", v)} testid="ast-serial" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Location" value={f.location} onChange={(v) => set("location", v)} testid="ast-location" />
            <SelF label="Condition" value={f.condition} onChange={(v) => set("condition", v)} options={(catalog?.conditions || []).map((c) => ({ v: c, label: c }))} testid="ast-condition" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <F label="Purchase date" type="date" value={f.purchase_date} onChange={(v) => set("purchase_date", v)} testid="ast-pdate" />
            <F label="Purchase cost" type="number" value={f.purchase_cost} onChange={(v) => set("purchase_cost", v)} testid="ast-pcost" />
            <F label="Currency" value={f.currency} onChange={(v) => set("currency", v)} testid="ast-currency" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <F label="Vendor" value={f.vendor_name} onChange={(v) => set("vendor_name", v)} testid="ast-vendor" />
            <F label="Vendor contact" value={f.vendor_contact} onChange={(v) => set("vendor_contact", v)} testid="ast-vcontact" />
            <F label="Invoice #" value={f.invoice_no} onChange={(v) => set("invoice_no", v)} testid="ast-invoice" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Warranty ends" type="date" value={f.warranty_end} onChange={(v) => set("warranty_end", v)} testid="ast-warranty" />
            <F label="Depreciation (years)" type="number" value={f.depreciation_years} onChange={(v) => set("depreciation_years", v)} testid="ast-depr" />
          </div>
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Notes</Label>
            <Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} className="rounded-none" data-testid="ast-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy} data-testid="ast-save">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div>
      <Label className="label-eyebrow mb-1.5 inline-block">{label}</Label>
      <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="h-9 rounded-none" placeholder={placeholder} data-testid={testid} />
    </div>
  );
}
function SelF({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="label-eyebrow mb-1.5 inline-block">{label}</Label>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger className="h-9 rounded-none" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function AssignDialog({ wsId, asset, mode, catalog, onClose, onDone }) {
  const [members, setMembers] = useState([]);
  const [userId, setUserId] = useState("");
  const [notes, setNotes] = useState("");
  const [condition, setCondition] = useState(asset.condition || "good");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (mode === "assign" && wsId) api.get(`/workspaces/${wsId}/members`).then((r) => setMembers(r.data || [])); }, [wsId, mode]);
  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "assign") {
        if (!userId) { toast.error("Pick a user"); setBusy(false); return; }
        await api.post(`/assets/${asset.id}/assign`, { user_id: userId, notes });
        toast.success("Assigned");
      } else {
        await api.post(`/assets/${asset.id}/return`, { condition, notes });
        toast.success("Returned to stock");
      }
      onDone();
    } catch (e) { toast.error(apiErrorMessage(e, "Action failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid={`ast-${mode}-dialog`}>
        <DialogHeader><DialogTitle>{mode === "assign" ? `Assign ${asset.name}` : `Return ${asset.name}`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {mode === "assign" ? (
            <div>
              <Label className="label-eyebrow mb-1.5 inline-block">Assign to</Label>
              <Select value={userId} onValueChange={setUserId}>
                <SelectTrigger className="h-9 rounded-none" data-testid="ast-assign-user"><SelectValue placeholder="Pick a user" /></SelectTrigger>
                <SelectContent>{members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.user?.name || m.user?.email}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ) : (
            <div>
              <Label className="label-eyebrow mb-1.5 inline-block">Return condition</Label>
              <Select value={condition} onValueChange={setCondition}>
                <SelectTrigger className="h-9 rounded-none" data-testid="ast-return-condition"><SelectValue /></SelectTrigger>
                <SelectContent>{(catalog?.conditions || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-none" data-testid={`ast-${mode}-notes`} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid={`ast-${mode}-submit`}>{busy ? "…" : mode === "assign" ? "Assign" : "Return"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssetDetailDialog({ assetId, catalog, canManage, onClose, onChange }) {
  const [data, setData] = useState(null);
  const [addingMaint, setAddingMaint] = useState(false);
  const [completingMaint, setCompletingMaint] = useState(null);
  const load = () => api.get(`/assets/${assetId}`).then((r) => setData(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [assetId]);
  if (!data) return <Dialog open onOpenChange={onClose}><DialogContent><div className="p-4 text-sm">Loading…</div></DialogContent></Dialog>;
  const a = data.asset;
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="asset-detail-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Package className="w-4 h-4" /> {a.name} <code className="text-xs font-mono text-foreground/60">{a.asset_tag}</code></DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <DT label="Category" value={a.category} />
          <DT label="Status" value={<Badge variant="outline" className={`rounded-none ${STATUS_STYLE[a.status] || ""}`}>{(a.status || "").replace(/_/g, " ")}</Badge>} />
          <DT label="Brand · Model" value={[a.brand, a.model].filter(Boolean).join(" · ") || "—"} />
          <DT label="Serial" value={a.serial_number || "—"} />
          <DT label="Location" value={a.location || "—"} />
          <DT label="Condition" value={a.condition || "—"} />
          <DT label="Purchase" value={a.purchase_date ? `${a.purchase_date} · ${a.currency} ${fmtMoney(a.purchase_cost)}` : "—"} />
          <DT label="Warranty ends" value={a.warranty_end || "—"} />
          <DT label="Vendor" value={a.vendor_name || "—"} />
          <DT label="Invoice #" value={a.invoice_no || "—"} />
          <DT label="Assigned to" value={a.assigned_to?.name || "—"} />
          <DT label="Depreciation" value={a.depreciation_years ? `${a.depreciation_years} years` : "—"} />
        </div>
        {a.notes && <div className="mt-3 text-xs border border-border p-3 whitespace-pre-wrap">{a.notes}</div>}

        <AssetAttachments assetId={assetId} canManage={canManage} />

        <div className="mt-5 border-t border-border pt-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="label-eyebrow">Maintenance ({data.maintenance.length})</h4>
            {canManage && <Button size="sm" className="rounded-none" onClick={() => setAddingMaint(true)} data-testid="ast-maint-new"><Plus className="w-3 h-3 mr-1" />Schedule</Button>}
          </div>
          {data.maintenance.length === 0 ? <p className="text-xs text-foreground/55">No maintenance records yet.</p>
            : (
              <div className="border border-border divide-y divide-border text-xs" data-testid="ast-maint-list">
                {data.maintenance.map((m) => (
                  <div key={m.id} className="px-3 py-2 flex items-center gap-3" data-testid={`maint-row-${m.id}`}>
                    <Wrench className="w-3.5 h-3.5 text-foreground/50" />
                    <div className="flex-1 min-w-0">
                      <div className="truncate"><strong>{m.title}</strong> <span className="text-foreground/55">· {m.kind}</span></div>
                      <div className="text-[10px] text-foreground/55">{m.scheduled_date ? `sched ${m.scheduled_date}` : ""}{m.completed_date ? ` · done ${m.completed_date}` : ""} · {m.status}</div>
                    </div>
                    <span className="font-mono text-[10px]">{m.cost ? `${m.currency || ""} ${m.cost}` : ""}</span>
                    {canManage && (m.status === "scheduled" || m.status === "in_progress") && (
                      <Button size="sm" variant="ghost" className="rounded-none h-6 px-2 text-[11px]" onClick={() => setCompletingMaint(m)} data-testid={`maint-complete-${m.id}`}>Done</Button>
                    )}
                  </div>
                ))}
              </div>
            )}
        </div>

        <div className="mt-5 border-t border-border pt-4">
          <h4 className="label-eyebrow mb-2">Assignment history ({data.history.length})</h4>
          {data.history.length === 0 ? <p className="text-xs text-foreground/55">Never assigned.</p>
            : (
              <div className="border border-border divide-y divide-border text-xs" data-testid="ast-history">
                {data.history.map((h) => (
                  <div key={h.id} className="px-3 py-2 flex items-center gap-3">
                    <UserPlus className="w-3.5 h-3.5 text-foreground/50" />
                    <div className="flex-1 min-w-0">
                      <strong>{h.user?.name || h.user_id || "—"}</strong>
                      <div className="text-[10px] text-foreground/55">assigned {(h.assigned_at || "").slice(0, 16).replace("T", " ")}{h.returned_at ? ` · returned ${(h.returned_at || "").slice(0, 16).replace("T", " ")} (${h.return_condition || "—"})` : " · still holding"}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </div>

        {addingMaint && <MaintEditor assetId={assetId} catalog={catalog} onClose={() => setAddingMaint(false)} onSaved={() => { setAddingMaint(false); load(); onChange?.(); }} />}
        {completingMaint && <MaintCompleteDialog maint={completingMaint} onClose={() => setCompletingMaint(null)} onSaved={() => { setCompletingMaint(null); load(); onChange?.(); }} />}
      </DialogContent>
    </Dialog>
  );
}

function DT({ label, value }) { return <div><div className="text-[10px] uppercase tracking-widest text-foreground/55">{label}</div><div className="font-medium mt-0.5">{value ?? "—"}</div></div>; }
function MaintCompleteDialog({ maint, onClose, onSaved }) {
  const [cost, setCost] = useState(maint.cost ?? "");
  const [notes, setNotes] = useState("");
  const [nextDue, setNextDue] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        cost: cost === "" ? null : Number(cost),
        notes: notes || null,
        next_due_date: nextDue || null,
      };
      await api.post(`/maintenance/${maint.id}/complete`, payload);
      toast.success("Maintenance completed");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="maint-complete-dialog">
        <DialogHeader><DialogTitle>Mark done · {maint.title}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Actual cost ({maint.currency || "INR"})</Label>
            <Input type="number" value={cost} onChange={(e) => setCost(e.target.value)} className="h-9 rounded-none" placeholder="0" data-testid="maint-complete-cost" />
          </div>
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Notes (optional)</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-none" placeholder="What was done?" data-testid="maint-complete-notes" />
          </div>
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Auto-schedule next cycle (optional)</Label>
            <Input type="date" value={nextDue} onChange={(e) => setNextDue(e.target.value)} className="h-9 rounded-none" data-testid="maint-complete-next-due" />
            <p className="text-[11px] text-foreground/55 mt-1">Setting a date creates a new scheduled maintenance for that day.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="maint-complete-submit">{busy ? "…" : "Mark done"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



function MaintEditor({ assetId, catalog, onClose, onSaved }) {
  const [f, setF] = useState({ kind: "preventive", title: "", scheduled_date: "", status: "scheduled", vendor: "", cost: "", currency: "INR", notes: "", next_due_date: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    try {
      const payload = { ...f };
      if (payload.cost === "") payload.cost = null; else payload.cost = Number(payload.cost);
      await api.post(`/assets/${assetId}/maintenance`, payload);
      toast.success("Scheduled");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(false); }
  };
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="maint-editor-dialog">
        <DialogHeader><DialogTitle>Schedule maintenance</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <F label="Title *" value={f.title} onChange={(v) => set("title", v)} placeholder="Battery replacement" testid="maint-title" />
          <div className="grid grid-cols-3 gap-3">
            <SelF label="Kind" value={f.kind} onChange={(v) => set("kind", v)} options={(catalog?.maintenance_kinds || []).map((k) => ({ v: k, label: k }))} testid="maint-kind" />
            <SelF label="Status" value={f.status} onChange={(v) => set("status", v)} options={(catalog?.maintenance_statuses || []).map((s) => ({ v: s, label: s.replace(/_/g, " ") }))} testid="maint-status" />
            <F label="Scheduled" type="date" value={f.scheduled_date} onChange={(v) => set("scheduled_date", v)} testid="maint-date" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <F label="Vendor" value={f.vendor} onChange={(v) => set("vendor", v)} testid="maint-vendor" />
            <F label="Cost" type="number" value={f.cost} onChange={(v) => set("cost", v)} testid="maint-cost" />
            <F label="Currency" value={f.currency} onChange={(v) => set("currency", v)} testid="maint-currency" />
          </div>
          <F label="Next due (recurring)" type="date" value={f.next_due_date} onChange={(v) => set("next_due_date", v)} testid="maint-next-due" />
          <div><Label className="label-eyebrow mb-1.5 inline-block">Notes</Label><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} className="rounded-none" data-testid="maint-notes" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="maint-save">{busy ? "…" : "Schedule"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ───────────────────── Maintenance queue ─────────────────────

function MaintenanceQueue({ wsId }) {
  const [win, setWin] = useState("upcoming");
  const [rows, setRows] = useState(null);
  useEffect(() => { if (wsId) api.get(`/workspaces/${wsId}/maintenance-schedule?window=${win}`).then((r) => setRows(r.data)); }, [wsId, win]);
  return (
    <div className="space-y-4" data-testid="maint-queue">
      <div className="flex gap-1">
        {["upcoming", "overdue", "all"].map((w) => (
          <button key={w} onClick={() => setWin(w)} className={`text-xs px-3 py-1.5 border rounded-none capitalize ${win === w ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-foreground/5"}`} data-testid={`maint-window-${w}`}>{w}</button>
        ))}
      </div>
      {!rows ? <Card className="rounded-none"><CardContent className="p-6 text-sm text-foreground/60">Loading…</CardContent></Card>
        : rows.length === 0 ? <Card className="rounded-none border-dashed"><CardContent className="p-10 text-center text-sm text-foreground/60" data-testid="maint-queue-empty">No {win} maintenance items. All caught up.</CardContent></Card>
        : (
          <div className="border border-border divide-y divide-border" data-testid="maint-queue-list">
            {rows.map((m) => (
              <div key={m.id} className="px-3 py-2 flex items-center gap-3 text-sm" data-testid={`maint-queue-${m.id}`}>
                <Wrench className="w-4 h-4 text-foreground/50" />
                <div className="flex-1 min-w-0">
                  <div className="truncate"><strong>{m.title}</strong> <span className="text-foreground/55">· {m.asset?.name || m.asset_id} ({m.asset?.asset_tag || ""})</span></div>
                  <div className="text-[11px] text-foreground/55">{m.kind} · sched {m.scheduled_date || "—"} · {m.status}</div>
                </div>
                <span className="font-mono text-xs">{m.cost ? `${m.currency || ""} ${m.cost}` : ""}</span>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
